import { FetchError, type ResponseType as FetchResponseType } from 'ofetch';

import { fetchFormatQuery } from '@lib/shared';
import lodashOmit from 'lodash-es/omit';

export type RequestOption = {
    headers?: Record<string, string>;
    signal?: AbortSignal;
};

export type RequestConfig = RequestOption & {
    url: string;
    method: 'HEAD' | 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    params?: Record<string, any>;
    data?: BodyType<unknown>;
    // Orval emits `responseType: 'blob'` for binary endpoints; it is declared here to satisfy the generated
    // call sites but deliberately not forwarded to $fetch, which would apply it to error responses too and
    // hand `BackendErrorEntity` json to the callers as an unparsed Blob.
    // ofetch already picks blob from the `application/pdf` content-type, per response.
    responseType?: FetchResponseType;
};

export const fetchApiInstance = <ResponseType>(
    { url, method, params, data, headers = {}, signal, responseType }: RequestConfig,
    options: RequestOption = {}
): Promise<ResponseType> => {
    const runtimeConfig = useRuntimeConfig();
    const { token } = useAuthUtils();

    return $fetch<ResponseType, string>(url, {
        baseURL: runtimeConfig.public.backendApiBaseUrl,
        method,
        query: fetchFormatQuery(params),
        // Remove Content-Type header as suggested in github issue https://github.com/unjs/ofetch/issues/37 ofetch insert it automatically
        headers: {
            ...(token.value ? { Authorization: token.value } : {}),
            ...lodashOmit(headers, ['Content-Type']),
            ...(options && options?.headers ? lodashOmit(options?.headers, ['Content-Type']) : {}),
        },
        ...(data ? { body: data } : {}),
        ...(responseType ? { responseType } : {}),
        signal: options?.signal ?? signal,
    });
};

export default fetchApiInstance;

/**
 * `data` is deliberately left `unknown`: ofetch parses the body by content-type and validates
 * nothing against the documented error shape, so a proxy error page or a bodiless 5xx lands here
 * just as well. Narrow it with `isBackendError` before reading it.
 *
 * The type parameter is unused, but Orval always instantiates this with one argument.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ErrorType<_Contract> = FetchError<unknown>;

export type BodyType<BodyData> = BodyData;
